import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CATEGORY_LABELS, type CategorySlug } from '../lib/types'

interface CategoryData {
  category: string
  count: number
  booths: string
  names: string
}

interface IndustryData {
  industry_focus: string
  count: number
  booths: string
  names: string
}

const CATEGORY_COLORS: Record<string, string> = {
  'robotics': '#ef4444',
  'ai-ml': '#8b5cf6',
  'machine-vision': '#3b82f6',
  'motion-control': '#f59e0b',
  'industrial-automation': '#10b981',
  'software-digital-twin': '#6366f1',
  'safety-compliance': '#f97316',
  'b2b-services': '#64748b',
  'sensors-iot': '#06b6d4',
  'logistics-warehousing': '#84cc16',
  'humanoid-robots': '#ec4899',
  'components-connectors': '#78716c',
  'education-training': '#a855f7',
  'system-integrators': '#14b8a6',
  'pneumatics-hydraulics': '#ea580c',
  'metrology-inspection': '#2563eb',
  'embedded-computing': '#7c3aed',
  'mechanical-components': '#737373',
  'amr-agv': '#dc2626',
  'grippers-eol': '#059669',
  'welding-joining': '#d97706',
  '3d-printing': '#0891b2',
  'startups': '#f43f5e',
  'automotive-ev': '#0d9488',
  'precision-machining': '#b45309',
  'medical-life-sciences': '#db2777',
  'energy-power': '#ca8a04',
  'material-handling': '#65a30d',
  'testing-qc': '#4f46e5',
  'laser-optics': '#e11d48',
  'other': '#94a3b8',
}

function getBoothRange(booths: string): string {
  if (!booths) return ''
  const nums = booths.split(',').map(b => parseInt(b.trim())).filter(n => !isNaN(n)).sort((a, b) => a - b)
  if (nums.length === 0) return ''
  if (nums.length <= 3) return nums.join(', ')
  return `${nums[0]}–${nums[nums.length - 1]}`
}

function getBoothAisles(booths: string): string[] {
  if (!booths) return []
  const nums = booths.split(',').map(b => parseInt(b.trim())).filter(n => !isNaN(n))
  const aisles = new Set(nums.map(n => {
    const s = String(n)
    if (s.length >= 4) return s.slice(0, 2) + '00s'
    if (s.length >= 3) return s.slice(0, 1) + '00s'
    return '< 100'
  }))
  return [...aisles].sort()
}

export default function IndustryMap() {
  const [data, setData] = useState<{ total: number; categories: CategoryData[]; industries: IndustryData[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'categories' | 'industries'>('categories')
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/industry-map')
      .then(r => r.json())
      .then(d => setData(d))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  if (!data) return <div className="text-center py-12 text-gray-500">Failed to load data</div>

  const cats = data.categories.filter(c => c.category !== 'other')
  const otherCat = data.categories.find(c => c.category === 'other')
  const maxCount = Math.max(...cats.map(c => c.count))

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">Industry Map</h1>
        <p className="text-gray-500 text-sm">
          {data.total} exhibitors across {data.categories.length} categories at Automate 2026
        </p>
      </div>

      {/* View toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('categories')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            view === 'categories' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          By Category
        </button>
        <button
          onClick={() => setView('industries')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            view === 'industries' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          By Industry Focus
        </button>
      </div>

      {view === 'categories' ? (
        <>
          {/* Treemap-style grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
            {cats.map(cat => {
              const label = CATEGORY_LABELS[cat.category as CategorySlug] || cat.category
              const color = CATEGORY_COLORS[cat.category] || '#94a3b8'
              const pct = Math.round((cat.count / data.total) * 100)
              const aisles = getBoothAisles(cat.booths)
              const isExpanded = expanded === cat.category

              return (
                <div
                  key={cat.category}
                  onClick={() => setExpanded(isExpanded ? null : cat.category)}
                  className="rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg"
                  style={{
                    backgroundColor: color + '15',
                    borderLeft: `4px solid ${color}`,
                  }}
                >
                  <div className="text-xs font-medium uppercase tracking-wide" style={{ color }}>{label}</div>
                  <div className="text-2xl font-bold mt-1" style={{ color }}>{cat.count}</div>
                  <div className="text-xs text-gray-500">{pct}% of exhibitors</div>
                  <div className="mt-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${(cat.count / maxCount) * 100}%`, backgroundColor: color }}
                      />
                    </div>
                  </div>
                  {aisles.length > 0 && (
                    <div className="text-xs text-gray-400 mt-2">
                      Aisles: {aisles.slice(0, 4).join(', ')}{aisles.length > 4 ? '...' : ''}
                    </div>
                  )}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-xs text-gray-500 mb-2">
                        Booths: {getBoothRange(cat.booths)}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {cat.names.split('|||').slice(0, 8).map((name, i) => (
                          <span key={i} className="text-xs bg-white rounded px-1.5 py-0.5 text-gray-600">{name}</span>
                        ))}
                        {cat.names.split('|||').length > 8 && (
                          <span className="text-xs text-gray-400">+{cat.names.split('|||').length - 8} more</span>
                        )}
                      </div>
                      <Link
                        to={`/companies?category=${cat.category}`}
                        className="text-xs text-indigo-600 hover:underline mt-2 inline-block"
                        onClick={e => e.stopPropagation()}
                      >
                        View all {cat.count} exhibitors &rarr;
                      </Link>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Other category */}
          {otherCat && (
            <div className="card bg-gray-50 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-600">Other / Uncategorized</span>
                  <span className="text-sm text-gray-400 ml-2">{otherCat.count} exhibitors</span>
                </div>
                <Link to="/companies?category=other" className="text-xs text-indigo-600 hover:underline">
                  View all &rarr;
                </Link>
              </div>
            </div>
          )}

          {/* Summary stats */}
          <div className="card">
            <h3 className="font-semibold mb-3">Category Summary</h3>
            <div className="space-y-2">
              {cats.map(cat => {
                const label = CATEGORY_LABELS[cat.category as CategorySlug] || cat.category
                const color = CATEGORY_COLORS[cat.category] || '#94a3b8'
                return (
                  <div key={cat.category} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <Link
                      to={`/companies?category=${cat.category}`}
                      className="text-sm text-gray-700 hover:text-indigo-600 flex-1"
                    >
                      {label}
                    </Link>
                    <span className="text-sm font-mono text-gray-500">{cat.count}</span>
                    <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(cat.count / maxCount) * 100}%`, backgroundColor: color }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      ) : (
        /* Industry Focus view */
        <div className="space-y-2">
          {data.industries.filter(i => i.count >= 1).map((ind, idx) => (
            <div
              key={idx}
              className="card py-3 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpanded(expanded === ind.industry_focus ? null : ind.industry_focus)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-800">{ind.industry_focus}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-500">{ind.count} {ind.count === 1 ? 'company' : 'companies'}</span>
                  <span className="text-xs text-gray-400">Booths: {getBoothRange(ind.booths)}</span>
                </div>
              </div>
              {expanded === ind.industry_focus && (
                <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-1">
                  {ind.names.split('|||').map((name, i) => (
                    <span key={i} className="text-xs bg-indigo-50 text-indigo-700 rounded px-2 py-0.5">{name}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
